import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calendar, Plus, Edit2, Trash2, Eye, EyeOff, 
  MapPin, Clock, X, Save, Star
} from 'lucide-react';
import './AdminEvents.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEvents = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [stats, setStats] = useState({ total: 0, upcoming: 0, past: 0 });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time_start: '',
    time_end: '',
    location: '',
    category: 'workshop',
    image: '',
    is_featured: false,
    registration_link: '',
    max_participants: ''
  });

  const categories = [
    { id: 'workshop', label: 'Workshop' },
    { id: 'palestra', label: 'Palestra' },
    { id: 'formacao', label: 'Formação' },
    { id: 'evento_social', label: 'Evento Social' },
    { id: 'outro', label: 'Outro' }
  ];

  useEffect(() => {
    loadEvents();
    loadStats();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/events/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: '',
      time_start: '',
      time_end: '',
      location: '',
      category: 'workshop',
      image: '',
      is_featured: false,
      registration_link: '',
      max_participants: ''
    });
    setEditingEvent(null);
  };

  const openModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        title: event.title || '',
        description: event.description || '',
        date: event.date || '',
        time_start: event.time_start || '',
        time_end: event.time_end || '',
        location: event.location || '',
        category: event.category || 'workshop',
        image: event.image || '',
        is_featured: event.is_featured || false,
        registration_link: event.registration_link || '',
        max_participants: event.max_participants || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      max_participants: formData.max_participants ? parseInt(formData.max_participants) : null
    };

    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/events`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      loadEvents();
      loadStats();
      closeModal();
    } catch (error) {
      console.error('Erro ao guardar evento:', error);
      alert('Erro ao guardar evento');
    }
  };

  const toggleActive = async (event) => {
    try {
      await axios.put(`${API}/events/${event.id}`, 
        { is_active: !event.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEvents(prev => prev.map(e => 
        e.id === event.id ? { ...e, is_active: !e.is_active } : e
      ));
    } catch (error) {
      console.error('Erro ao atualizar evento:', error);
    }
  };

  const toggleFeatured = async (event) => {
    try {
      await axios.put(`${API}/events/${event.id}`, 
        { is_featured: !event.is_featured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEvents(prev => prev.map(e => 
        e.id === event.id ? { ...e, is_featured: !e.is_featured } : e
      ));
    } catch (error) {
      console.error('Erro ao atualizar evento:', error);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Tem a certeza que deseja eliminar este evento?')) return;
    
    try {
      await axios.delete(`${API}/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(prev => prev.filter(e => e.id !== id));
      loadStats();
    } catch (error) {
      console.error('Erro ao eliminar evento:', error);
      alert('Erro ao eliminar evento');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-PT');
  };

  const getCategoryLabel = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.label : categoryId;
  };

  const isUpcoming = (dateStr) => {
    return new Date(dateStr) >= new Date(new Date().toDateString());
  };

  if (loading) {
    return <div className="loading">A carregar eventos...</div>;
  }

  return (
    <div className="admin-events" data-testid="admin-events">
      <div className="page-header">
        <div>
          <h1><Calendar size={24} /> Gestão de Eventos</h1>
          <p>Criar e gerir eventos e atividades</p>
        </div>
        <button className="add-btn" onClick={() => openModal()}>
          <Plus size={20} />
          Novo Evento
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-number">{stats.total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-card upcoming">
          <span className="stat-number">{stats.upcoming}</span>
          <span className="stat-label">Próximos</span>
        </div>
        <div className="stat-card past">
          <span className="stat-number">{stats.past}</span>
          <span className="stat-label">Passados</span>
        </div>
      </div>

      {/* Events Table */}
      {events.length === 0 ? (
        <div className="empty-state">
          <Calendar size={48} />
          <p>Ainda não existem eventos.</p>
          <button className="add-btn" onClick={() => openModal()}>
            <Plus size={18} /> Criar primeiro evento
          </button>
        </div>
      ) : (
        <div className="events-table-wrapper">
          <table className="events-table">
            <thead>
              <tr>
                <th>Evento</th>
                <th>Data</th>
                <th>Local</th>
                <th>Categoria</th>
                <th>Estado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.id} className={!event.is_active ? 'inactive' : ''}>
                  <td className="event-title-cell">
                    <div className="event-title-wrapper">
                      {event.is_featured && <Star size={14} className="star-icon" />}
                      <span>{event.title}</span>
                    </div>
                  </td>
                  <td>
                    <span className={isUpcoming(event.date) ? 'upcoming-date' : 'past-date'}>
                      {formatDate(event.date)}
                    </span>
                  </td>
                  <td>
                    <div className="location-cell">
                      <MapPin size={14} />
                      {event.location}
                    </div>
                  </td>
                  <td>
                    <span className="category-badge">{getCategoryLabel(event.category)}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${event.is_active ? 'active' : 'inactive'}`}>
                      {event.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <button 
                        className="action-btn star" 
                        onClick={() => toggleFeatured(event)}
                        title={event.is_featured ? 'Remover destaque' : 'Destacar'}
                      >
                        <Star size={16} fill={event.is_featured ? '#f59e0b' : 'none'} />
                      </button>
                      <button 
                        className="action-btn visibility" 
                        onClick={() => toggleActive(event)}
                        title={event.is_active ? 'Desativar' : 'Ativar'}
                      >
                        {event.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                      <button 
                        className="action-btn edit" 
                        onClick={() => openModal(event)}
                        title="Editar"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        className="action-btn delete" 
                        onClick={() => deleteEvent(event.id)}
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEvent ? 'Editar Evento' : 'Novo Evento'}</h2>
              <button className="close-btn" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="event-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Título *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row two-cols">
                <div className="form-group">
                  <label>Data *</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Categoria *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row two-cols">
                <div className="form-group">
                  <label>Hora Início *</label>
                  <input
                    type="time"
                    name="time_start"
                    value={formData.time_start}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Hora Fim</label>
                  <input
                    type="time"
                    name="time_end"
                    value={formData.time_end}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Local *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Ex: Sede AASPS, Lisboa"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Descrição *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="4"
                    required
                  />
                </div>
              </div>

              <div className="form-row two-cols">
                <div className="form-group">
                  <label>Link de Inscrição</label>
                  <input
                    type="url"
                    name="registration_link"
                    value={formData.registration_link}
                    onChange={handleInputChange}
                    placeholder="https://..."
                  />
                </div>
                <div className="form-group">
                  <label>Máx. Participantes</label>
                  <input
                    type="number"
                    name="max_participants"
                    value={formData.max_participants}
                    onChange={handleInputChange}
                    min="1"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>URL da Imagem</label>
                  <input
                    type="url"
                    name="image"
                    value={formData.image}
                    onChange={handleInputChange}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleInputChange}
                  />
                  <span>Destacar este evento</span>
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="save-btn">
                  <Save size={18} />
                  {editingEvent ? 'Guardar Alterações' : 'Criar Evento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
